import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GA_MEASUREMENT_ID, isGA4Initialized } from '@/lib/analytics';
import { useCookieConsent } from '@/contexts/CookieContext';

/**
 * Google Analytics Page View Tracker
 * 
 * This component tracks route changes in the React Router SPA and sends
 * page_view events to Google Analytics 4.
 * 
 * It tracks all accessible pages including:
 * - Initial page load
 * - Route changes (including dynamic routes like /services/:serviceId)
 * - 404 pages
 * - All navigation events
 * 
 * It only tracks when:
 * 1. Analytics consent has been given
 * 2. GA4 is initialized
 * 3. The GA Measurement ID is configured
 */
const GATracker = () => {
  const location = useLocation();
  const { consent } = useCookieConsent();
  const previousPathRef = useRef<string>('');

  useEffect(() => {
    // Only track if analytics consent is given
    if (!consent?.preferences.analytics) {
      return;
    }

    // Only track if GA4 is initialized
    if (!isGA4Initialized()) {
      return;
    }

    // Only track if GA Measurement ID is configured
    if (!GA_MEASUREMENT_ID) {
      console.warn('GA4 Measurement ID is not configured. Page view not tracked.');
      return;
    }

    // Get the current path and title
    const newPath = location.pathname + location.search;
    const newHash = location.hash || '';
    const fullPath = newPath + newHash;

    // Skip if this is the same path (e.g., hash changes only)
    if (previousPathRef.current === fullPath) {
      return;
    }

    // Update previous path
    previousPathRef.current = fullPath;

    // Function to send page view with retry logic for title
    const sendPageView = (retryCount = 0) => {
      // Get title - use a fallback if not set yet
      let pageTitle = document.title;
      
      // If title is still the default or empty, wait a bit for SEO component to set it
      if ((!pageTitle || pageTitle === 'Byteet - Professional IT Services | Endpoint Management & Cloud Solutions') && retryCount < 3) {
        setTimeout(() => {
          sendPageView(retryCount + 1);
        }, 100 * (retryCount + 1)); // Exponential backoff: 100ms, 200ms, 300ms
        return;
      }

      // Fallback title if still not set
      if (!pageTitle || pageTitle.trim() === '') {
        // Generate title from path
        const pathParts = location.pathname.split('/').filter(Boolean);
        if (pathParts.length === 0) {
          pageTitle = 'Home - Byteet';
        } else {
          const lastPart = pathParts[pathParts.length - 1];
          pageTitle = `${lastPart.charAt(0).toUpperCase() + lastPart.slice(1).replace(/-/g, ' ')} - Byteet`;
        }
      }

      // Check if gtag is available
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        // Send page_view event to Google Analytics
        window.gtag('config', GA_MEASUREMENT_ID, {
          page_path: newPath,
          page_title: pageTitle,
          anonymize_ip: true,
        });

        // Also send a page_view event for better tracking
        window.gtag('event', 'page_view', {
          page_path: newPath,
          page_title: pageTitle,
          page_location: window.location.href,
        });
      }
    };

    // Send page view (with retry logic for title)
    sendPageView();
  }, [location.pathname, location.search, location.hash, consent?.preferences.analytics]); // Track all location changes

  return null; // This component doesn't render anything visible
};

export default GATracker;

